import { SEGMENT_VERDICTS, SegmentVerdict, UNRESOLVED_REASONS, UnresolvedReason } from '@pelicargo/types';

/**
 * The booking-confirmation draft payload, as the review pane reads it. Typed and narrowed defensively rather than cast:
 * `RequestDraft.payload` is a Json column, so its shape is whatever the extractor wrote when the draft was created —
 * including drafts written by an older version of that extractor.
 */

export type ReconciledSegment = {
  extracted?: {
    flight_number?: string | null;
    origin?: string | null;
    destination?: string | null;
    arrival_day_offset?: number | null;
  } | null;
  flightNumber?: string | null;
  departureDate?: string | null;
  departureTime?: string | null;
  /**
   * The arrival date the api resolved, offset included — what an apply would write. Absent on drafts written before the
   * extractor resolved it; the table then falls back to printing the raw `+N` the carrier stated.
   */
  arrivalDate?: string | null;
  arrivalTime?: string | null;
  flightKey?: string | null;
  unresolved?: string | null;
  verdict?: string | null;
  quoteSegmentId?: number | null;
};

export type PayloadCutoff = {
  raw_date?: string | null;
  raw_time?: string | null;
  date?: string | null;
  time?: string | null;
};

/** Resolved value when code could read the token, else the raw print. */
export const formatCutoff = (cutoff: PayloadCutoff): string | null => {
  const date = cutoff.date ?? cutoff.raw_date;
  const time = cutoff.time ?? cutoff.raw_time;
  const text = [date, time].filter(Boolean).join(' ');
  return text || null;
};

export type BookingConfirmationPayload = {
  kind?: string | null;
  awb?: string | null;
  cutoff?: PayloadCutoff | null;
  /**
   * How the quote the verdicts compare against was resolved: 'awb' means the email named it, 'guessed' means linkage
   * picked the most plausible quote for the request+carrier. Null/absent on older drafts and when no quote matched.
   */
  quoteLinkage?: 'awb' | 'guessed' | null;
  segments: ReconciledSegment[];
  unconfirmedQuoteSegmentIds: number[];
  detailsInAttachment: boolean;
  attachmentsSkippedForBudget?: boolean;
  attachmentText?: string | null;
  notes?: string | null;
  /** The delivery address the email printed, checked against the quote's own in the linkage card. */
  deliveryAddress?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Null when the draft carries no booking payload (an older or failed extraction). */
export const readBookingPayload = (payload: unknown): BookingConfirmationPayload | null => {
  if (!isRecord(payload)) return null;
  if (!Array.isArray(payload.segments)) return null;

  return {
    kind: typeof payload.kind === 'string' ? payload.kind : null,
    awb: typeof payload.awb === 'string' ? payload.awb : null,
    cutoff: isRecord(payload.cutoff) ? (payload.cutoff as PayloadCutoff) : null,
    quoteLinkage: payload.quoteLinkage === 'awb' || payload.quoteLinkage === 'guessed' ? payload.quoteLinkage : null,
    segments: payload.segments.filter(isRecord) as ReconciledSegment[],
    unconfirmedQuoteSegmentIds: Array.isArray(payload.unconfirmedQuoteSegmentIds)
      ? payload.unconfirmedQuoteSegmentIds.filter((id): id is number => typeof id === 'number')
      : [],
    detailsInAttachment: payload.detailsInAttachment === true,
    attachmentsSkippedForBudget: payload.attachmentsSkippedForBudget === true,
    attachmentText: typeof payload.attachmentText === 'string' ? payload.attachmentText : null,
    notes: typeof payload.notes === 'string' ? payload.notes : null,
    deliveryAddress: typeof payload.deliveryAddress === 'string' ? payload.deliveryAddress : null,
  };
};

/**
 * Narrow a payload string to a verdict this build knows how to render. A draft written by a newer extractor may carry a
 * verdict this UI has never heard of; showing it as "not compared" beats crashing the pane.
 */
export const asVerdict = (value: unknown): SegmentVerdict | null =>
  SEGMENT_VERDICTS.includes(value as SegmentVerdict) ? (value as SegmentVerdict) : null;

export const asUnresolvedReason = (value: unknown): UnresolvedReason | null =>
  UNRESOLVED_REASONS.includes(value as UnresolvedReason) ? (value as UnresolvedReason) : null;

export type ApplySummaryFill = {
  quoteSegmentId: number | null;
  flightNumber: string | null;
  departsAt: string | null;
  arrivesAt: string | null;
};

export type ApplySummary = {
  appliedAt: string | null;
  isAuto: boolean;
  filled: ApplySummaryFill[];
  skipped: { flightKey: string | null; reason: string | null }[];
  comparisons: {
    quoteSegmentId: number | null;
    outcome: string | null;
    oldValue: string | null;
    newValue: string | null;
  }[];
};

/**
 * The stored record of what an apply did — read defensively like the payload, since it is a Json column written by
 * whichever service version applied it.
 */
export const readApplySummary = (value: unknown): ApplySummary | null => {
  if (!isRecord(value)) return null;

  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

  return {
    appliedAt: str(value.appliedAt),
    // `auto` is the pre-rename key: summaries stored before isAuto shipped still carry it.
    isAuto: value.isAuto === true || value.auto === true,
    filled: (Array.isArray(value.filled) ? value.filled : []).filter(isRecord).map((f) => ({
      quoteSegmentId: num(f.quoteSegmentId),
      flightNumber: str(f.flightNumber),
      departsAt: str(f.departsAt),
      arrivesAt: str(f.arrivesAt),
    })),
    skipped: (Array.isArray(value.skipped) ? value.skipped : []).filter(isRecord).map((s) => ({
      flightKey: str(s.flightKey),
      reason: str(s.reason),
    })),
    comparisons: (Array.isArray(value.comparisons) ? value.comparisons : []).filter(isRecord).map((c) => ({
      quoteSegmentId: num(c.quoteSegmentId),
      outcome: str(c.outcome),
      oldValue: str(c.oldValue),
      newValue: str(c.newValue),
    })),
  };
};
